import React, { SetStateAction, useEffect, useState } from "react";
import { TouchableOpacity, TextInput, Platform, TextStyle } from "react-native";
import { countries, _getCountry } from "./_lib/enhanced";
import { useTheme } from "styled-components/native";
import styled from "styled-components/native";
import { css } from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { CountryCodeProps } from "./types.js";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useTranslation } from "react-i18next";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import Modal from "../Modal";
import { VDBText } from "..";
import CountryFlag from "../CountryFlag";
import { DefaultTheme } from "styled-components/native";
interface CountryData {
  name: string;
  dial_code: string;
  code: string;
  flag: string;
}

const CountryCodeDropdownPicker = ({
  selected,
  setSelected,
  isGuest,
  setCountryDetails = () => {},
  phone,
  setPhone,
  phoneStyles = {},
  dropdownStyles = {},
  pickerIsOpen,
  fromBulkList = false,
  defaultValue = "",
}: CountryCodeProps) => {
  const [_selected, _setSelected] = React.useState(false);
  const [_search, _setSearch] = React.useState("");
  const [_countries, _setCountries] = React.useState(countries);
  const theme = useTheme();
  const typography = useTypographyStore();
  const { t } = useTranslation();
  const userInfo = useUserInfo();
  const [selectedCountry, setSelectedCountry] = useState<CountryData | null>(
    null
  );
  const userLoggedIn =
    (!!userInfo.user.country || !!userInfo.user.phone_number_country_code) &&
    !isGuest &&
    !fromBulkList;

  useEffect(() => {
    const fetchCountryData = async () => {
      let countryData;
      if (userInfo.user.country && !fromBulkList) {
        countryData = _getCountry(userInfo.user.country.toLowerCase());
      }
      if (
        !countryData &&
        userInfo.user.phone_number_country_code &&
        !fromBulkList
      ) {
        countryData = await _getCountry(
          "+" + userInfo.user.phone_number_country_code
        );
      }
      if (!countryData && !userLoggedIn && !fromBulkList) {
        countryData = _getCountry("US");
      }
      if (selected) {
        countryData = _getCountry("+" + selected);
      }
      if (fromBulkList) {
        countryData = _getCountry("+" + defaultValue);
      }
      if (countryData) {
        setSelectedCountry(countryData as CountryData);
      }
    };
    fetchCountryData();
  }, [
    userInfo.user.country,
    userInfo.user.phone_number_country_code,
    selected,
  ]);

  const slideDown = () => {
    _setSelected(true);
    pickerIsOpen && pickerIsOpen(true);
  };

  const slideUp = () => {
    _setSelected(false);
    _setSearch("");
    pickerIsOpen && pickerIsOpen(false);
  };

  const _searchCountry = (text: string) => {
    _setSearch(text);
    let c = countries.filter((item: CountryData) => {
      return item.name.toLowerCase().includes(text.toLowerCase());
    });
    _setCountries(c);
  };

  const RenderBtn = () => {
    if (!_selected) {
      return (
        <InputContainer>
          <TouchableOpacity
            style={{ flexDirection: "row" }}
            disabled={userLoggedIn}
            onPress={() => {
              _setCountries(countries);
              slideDown();
            }}
          >
            <SelectedCountryContainer
              disabled={userLoggedIn}
              fromBulkList={fromBulkList}
            >
              <VDBText
                type={typography["Body-R"]}
                color={userLoggedIn ? theme["core-04"] : theme["core-06"]}
                textAlign="left"
              >
                {t("contactUs.input.countryCode.placeholder.text")}
              </VDBText>
              <CountryValueContainer>
                {selectedCountry && (
                  <CountryFlag isoCode={selectedCountry?.code} size={18} />
                )}
                <VDBText
                  type={typography["Body-M"]}
                  color={userLoggedIn ? theme["core-04"] : theme["core-05"]}
                >
                  {selectedCountry?.dial_code}
                </VDBText>
              </CountryValueContainer>
            </SelectedCountryContainer>
          </TouchableOpacity>
          {phone !== undefined && setPhone !== undefined && (
            <TextInput
              style={[
                {
                  marginLeft: 5,
                  paddingVertical: 5,
                  paddingLeft: 15,
                  flex: 1,
                  borderWidth: 1,
                  borderRadius: 8,
                  borderColor: "#dddddd",
                },
                phoneStyles as TextStyle,
              ]}
              placeholder={"Enter Mobile Number"}
              keyboardType={"phone-pad"}
              placeholderTextColor={"#dddddd"}
              onChangeText={setPhone}
              value={phone}
            />
          )}
        </InputContainer>
      );
    } else {
      renderTextInput();
    }
  };

  const renderTextInput = () => {
    return (
      <InputBoxContainer>
        <InputContainer style={[{ width: "90%" }]}>
          <IconButtonContainer isLeftIcon>
            <VDBIcon
              color={theme["core-05"]}
              name={"main-search"}
              size={24}
              variant={IconsVariant.VDBCustom}
            />
          </IconButtonContainer>
          <TextInputContainer onChangeText={_searchCountry} value={_search} />
        </InputContainer>
      </InputBoxContainer>
    );
  };

  const renderCountryItem = ({ item }: { item: CountryData }) => {
    return (
      <CountryContainer
        key={item.code}
        onPress={() => {
          setSelected(item.dial_code.replace("+", ""));
          setCountryDetails?.(item as unknown as SetStateAction<undefined>);
          slideUp();
        }}
      >
        <CountryFlag isoCode={item?.code} size={18} />
        <VDBText type={typography["Body-R"]} color={"option-cont"}>
          {item?.name}
        </VDBText>
      </CountryContainer>
    );
  };

  return (
    <>
      <Container>{RenderBtn()}</Container>
      <Modal
        headerTitle={t("contactUs.input.countryCode.placeholder.text")}
        isOpen={_selected}
        onClose={() => slideUp()}
        paddingLeft={8}
        paddingRight={8}
        paddingBottom={0}
        webHeight={"80vh"}
        paddingTop={16}
        skipKeyboardAware={
          theme.isLargeScreen || Platform.OS === "web" ? true : false
        }
        showScrollbar={false}
      >
        {renderTextInput()}
        {_selected && (
          <FlatListContainer
            data={_countries}
            style={[dropdownStyles]}
            showsVerticalScrollIndicator={false}
            renderItem={renderCountryItem}
            keyExtractor={(item: CountryData) => item.code}
            ListEmptyComponent={
              <EmptyResultView>
                <VDBText
                  type={typography["Body-R"]}
                  color={"core-05"}
                  textAlign={"center"}
                >
                  No Result Found
                </VDBText>
              </EmptyResultView>
            }
          />
        )}
      </Modal>
    </>
  );
};

export default CountryCodeDropdownPicker;

const Container = styled.View`
  width: 100%;
`;

const EmptyResultView = styled.View`
  padding: ${SpaceType.Double}px;
`;

const IconButtonContainer = styled.View<{
  isLeftIcon?: boolean;
}>`
  height: 24px;
  width: 24px;
  ${({ isLeftIcon }: { isLeftIcon?: boolean }) =>
    isLeftIcon &&
    css`
      margin-left: 10;
    `}
`;

const TextInputContainer = styled.TextInput`
  flex: 1;
  height: 50px;
  margin-left: 5px;
  padding-vertical: 3px;
  outline-style: none;
  outline: none;
`;

const InputBoxContainer = styled.View`
  width: 100%;
  border-width: 1px;
  border-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-04"]};
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-radius: ${SpaceType.Basic}px;
`;

const InputContainer = styled.View`
  flex-direction: row;
  align-items: center;
`;

const CountryContainer = styled.TouchableOpacity`
  flex-direction: row;
  padding-horizontal: ${SpaceType.Double}px;
  padding-vertical: ${SpaceType.Extra}px;
  border-bottom-width: 1px;
  border-bottom-color: #dedede;
  align-items: center;
  gap: ${SpaceType.Basic}px;
`;

const FlatListContainer = styled.FlatList`
  border-width: 1px;
  border-color: #dddddd;
  border-radius: ${SpaceType.Basic}px;
  background-color: white;
  margin-top: 10px;
`;

const SelectedCountryContainer = styled.View<{
  disabled: boolean;
  fromBulkList?: boolean;
}>`
  padding-left: 10px;
  padding-right: 10px;
  min-width: ${({ fromBulkList }: { fromBulkList?: boolean }) =>
    fromBulkList ? "100%" : "20%"};
  align-items: left;
  justify-content: center;
  border-width: 1;
  border-color: ${({
    theme,
    disabled,
  }: {
    theme: DefaultTheme;
    disabled?: boolean;
  }) => (disabled ? theme["core-03"] : theme["core-04"])};
  border-radius: ${SpaceType.Basic}px;
  background-color: ${({
    theme,
    disabled,
  }: {
    theme: DefaultTheme;
    disabled?: boolean;
  }) => (disabled ? theme["core-02"] : theme["core-01"])};
  height: 56px;
`;

const CountryValueContainer = styled.View`
  flex-direction: row;
  padding-top: 4px;
  align-items: center;
  gap: 4px;
`;
